import React, { memo, useMemo, useState } from "react";
import MapEmbed from "../../maps/MapEmbed";
import { toast } from "react-toastify";
import { enrichCustomer } from "../../../api/customersApi";
import "./Info-tab.css";

const formatShortDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatCurrency = (value) => {
  if (value == null || !Number.isFinite(value)) return "";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `$${value}`;
  }
};

function readFieldValue(entity, name, fallback = "") {
  if (!entity || !name) return fallback ?? "";

  const candidates = [
    entity[name],
    // mapEntity() lifts only full_name/phones/emails to the top and parks the
    // full API record under .raw — customer cards rendered empty name/address
    // boxes because this chain never looked there.
    entity.raw?.[name],
    entity.extended_fields?.[name],
    entity.raw?.extended_fields?.[name],
    entity.attributes?.[name],
    entity.intake_attributes?.[name],
  ];

  for (const value of candidates) {
    if (value !== undefined && value !== null && value !== "") return value;
  }

  return fallback ?? "";
}

function cleanText(value) {
  return String(value || "").trim();
}

function buildAddressQuery(entity) {
  const street = cleanText(readFieldValue(entity, "address"));
  const city = cleanText(readFieldValue(entity, "city"));
  const state = cleanText(readFieldValue(entity, "state"));
  const zip = cleanText(readFieldValue(entity, "zip_code"));
  const county = cleanText(readFieldValue(entity, "county"));
  const country = cleanText(readFieldValue(entity, "country"));

  const locality = [city, state, zip].filter(Boolean).join(" ");
  return [street, locality, county, country].filter(Boolean).join(", ");
}

const InfoField = memo(function InfoField({
  label,
  name,
  value,
  onValueChange,
  type = "text",
  textarea = false,
  className = "",
  options,
}) {
  return (
    <div className={`info-field ${className}`.trim()}>
      <label className="info-label" htmlFor={`field-${name}`}>
        {label}
      </label>

      {options ? (
        <select
          id={`field-${name}`}
          className="info-input"
          value={value}
          onChange={(event) => onValueChange(name, event.target.value)}
        >
          <option value="">Select...</option>
          {options.map((option) => (
            <option
              key={option.value || option}
              value={option.value || option}
            >
              {option.label || option}
            </option>
          ))}
        </select>
      ) : textarea ? (
        <textarea
          id={`field-${name}`}
          className="info-input info-textarea"
          value={value}
          onChange={(event) => onValueChange(name, event.target.value)}
        />
      ) : (
        <input
          id={`field-${name}`}
          type={type}
          className="info-input"
          value={value}
          onChange={(event) => onValueChange(name, event.target.value)}
        />
      )}
    </div>
  );
});

const InfoMapPanel = memo(function InfoMapPanel({ addressQuery }) {
  if (!addressQuery) {
    return (
      <div className="info-map-empty">
        Add the property address to preview the location.
      </div>
    );
  }

  // One full map panel, FieldRoutes-style: marker on the property,
  // Streets/Satellite toggle, and the pegman for on-demand street view —
  // drag him onto any street to walk the neighborhood, exit back to the map.
  return <MapEmbed address={addressQuery} height={240} streetView zoom={18} />;
});

export default function InfoTab({ lead, formData = {}, onChange, preferences, hideMap = false }) {
  const [loadingEnrich, setLoadingEnrich] = useState(false);

  const safeLead = useMemo(
    () => ({ ...(lead || {}), ...(formData || {}) }),
    [lead, formData]
  );
  const isLoading = !lead;

  const isCustomer =
    safeLead.object === "customer" || safeLead.customer_id != null;

  const dynamicFields = useMemo(() => {
    const overrides =
      preferences?.ai_mapping_overrides?.lead_form ||
      preferences?.ai_mapping_overrides?.customer_form ||
      preferences?.ai_mapping_overrides?.customer_import ||
      {};
    return Object.entries(overrides).map(([label, name]) => ({
      label,
      name,
    }));
  }, [preferences]);

  const addressQuery = useMemo(() => buildAddressQuery(safeLead), [safeLead]);
  const leadNotes = cleanText(
    readFieldValue(safeLead, "notes", readFieldValue(safeLead, "message"))
  );
  const leadSummary = cleanText(readFieldValue(safeLead, "summary"));
  const leadStatus = cleanText(readFieldValue(safeLead, "status", "new"));
  const leadAttribution = useMemo(() => {
    const raw = safeLead?.attributes?.attribution;
    return raw && typeof raw === "object" ? raw : null;
  }, [safeLead]);
  const mapHref = addressQuery
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        addressQuery
      )}`
    : "";

  const updateField = (field, value) => {
    onChange?.({ target: { name: field, value } });
  };

  const IndustryFields = () => {
    switch (safeLead.industry) {
      case "landscaping":
        return (
          <>
            <InfoField
              label="Project Type"
              name="project_type"
              value={readFieldValue(safeLead, "project_type")}
              onValueChange={updateField}
            />
            <InfoField
              label="Square Footage"
              name="square_footage"
              value={readFieldValue(safeLead, "square_footage")}
              onValueChange={updateField}
            />
            <InfoField
              label="Budget"
              name="budget"
              value={readFieldValue(safeLead, "budget")}
              onValueChange={updateField}
            />
            <InfoField
              label="Deadline"
              name="deadline"
              value={readFieldValue(safeLead, "deadline")}
              onValueChange={updateField}
            />
          </>
        );

      case "pest":
      case "pest_control":
        return (
          <>
            <InfoField
              label="Service Type"
              name="service_type"
              value={readFieldValue(
                safeLead,
                "service_type",
                readFieldValue(safeLead, "service")
              )}
              onValueChange={updateField}
            />
            <InfoField
              label="Irrigation Type"
              name="irrigation_type"
              value={readFieldValue(safeLead, "irrigation_type")}
              onValueChange={updateField}
            />
            <InfoField
              label="Last Treatment"
              name="last_treatment"
              value={readFieldValue(safeLead, "last_treatment")}
              onValueChange={updateField}
            />
            <InfoField
              label="Notes"
              name="notes"
              value={readFieldValue(
                safeLead,
                "notes",
                readFieldValue(safeLead, "message")
              )}
              onValueChange={updateField}
              textarea
            />
          </>
        );

      case "cleaning":
        return (
          <>
            <InfoField
              label="Cleaning Type"
              name="cleaning_type"
              value={readFieldValue(safeLead, "cleaning_type")}
              onValueChange={updateField}
            />
            <InfoField
              label="Frequency"
              name="frequency"
              value={readFieldValue(safeLead, "frequency")}
              onValueChange={updateField}
            />
            <InfoField
              label="Pets"
              name="pets"
              value={readFieldValue(safeLead, "pets")}
              onValueChange={updateField}
            />
            <InfoField
              label="Notes"
              name="notes"
              value={readFieldValue(
                safeLead,
                "notes",
                readFieldValue(safeLead, "message")
              )}
              onValueChange={updateField}
              textarea
            />
          </>
        );

      case "real_estate":
        return (
          <>
            <InfoField
              label="Property Type"
              name="property_type"
              value={readFieldValue(safeLead, "property_type")}
              onValueChange={updateField}
            />
            <InfoField
              label="Bedrooms"
              type="number"
              name="number_of_bedrooms"
              value={readFieldValue(safeLead, "number_of_bedrooms")}
              onValueChange={updateField}
            />
            <InfoField
              label="Bathrooms"
              type="number"
              name="number_of_bathrooms"
              value={readFieldValue(safeLead, "number_of_bathrooms")}
              onValueChange={updateField}
            />
            <InfoField
              label="Condition"
              name="condition"
              value={readFieldValue(safeLead, "condition")}
              onValueChange={updateField}
            />
          </>
        );

      case "events":
        return (
          <>
            <InfoField
              label="Event Type"
              name="event_type"
              value={readFieldValue(safeLead, "event_type")}
              onValueChange={updateField}
            />
            <InfoField
              label="Event Date"
              name="event_date"
              type="date"
              value={readFieldValue(safeLead, "event_date")}
              onValueChange={updateField}
            />
            <InfoField
              label="Guest Count"
              name="guest_count"
              type="number"
              value={readFieldValue(safeLead, "guest_count")}
              onValueChange={updateField}
            />
            <InfoField
              label="Venue Preference"
              name="venue_preference"
              value={readFieldValue(safeLead, "venue_preference")}
              onValueChange={updateField}
            />
            <InfoField
              label="Budget Range"
              name="budget_range"
              value={readFieldValue(safeLead, "budget_range")}
              onValueChange={updateField}
            />
          </>
        );

      default:
        return <div className="info-none">No additional fields.</div>;
    }
  };

  const runEnrichment = async () => {
    if (!isCustomer) return toast.info("Convert to customer first.");

    try {
      setLoadingEnrich(true);
      const res = await enrichCustomer(safeLead.customer_id || safeLead.id);
      const data = res?.data || {};
      setLoadingEnrich(false);

      if (data?.error) return toast.error(data.error || "Enrichment failed.");
      toast.success("Enrichment complete.");
    } catch (err) {
      setLoadingEnrich(false);
      toast.error("Enrichment error.");
    }
  };

  if (isLoading) {
    return <div className="info-empty">Loading...</div>;
  }

  if (safeLead.object === "lead") {
    const statusOptions = [
      { value: "new", label: "New" },
      { value: "contacted", label: "Contacted" },
      { value: "qualified", label: "Qualified" },
      { value: "scheduled", label: "Scheduled" },
      { value: "closed", label: "Closed" },
      { value: "dead", label: "Dead" },
    ];

    return (
      <div className="info-tab">
        <div className="info-main-row info-main-row--lead">
          <div className="info-main-left">
            <div className="info-group info-group--dense">
              <div className="info-section-header">
                <div>
                  <div className="info-section-eyebrow">Lead record</div>
                  <div className="info-section-title">Customer Information</div>
                </div>
                <span className="info-status-chip">
                  {leadStatus ? leadStatus.toUpperCase() : "NEW"}
                </span>
              </div>

              <div className="info-grid-2 info-grid-tight">
                <InfoField
                  label="First Name"
                  name="first_name"
                  value={readFieldValue(safeLead, "first_name")}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Last Name"
                  name="last_name"
                  value={readFieldValue(safeLead, "last_name")}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Company Name"
                  name="company_name"
                  value={readFieldValue(safeLead, "company_name")}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Primary Phone"
                  name="primary_phone"
                  type="tel"
                  value={readFieldValue(
                    safeLead,
                    "primary_phone",
                    readFieldValue(safeLead, "phone_number")
                  )}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Primary Email"
                  name="primary_email"
                  type="email"
                  value={readFieldValue(
                    safeLead,
                    "primary_email",
                    readFieldValue(safeLead, "email")
                  )}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Service Type"
                  name="service_type"
                  value={readFieldValue(
                    safeLead,
                    "service_type",
                    readFieldValue(safeLead, "service")
                  )}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Lead Source"
                  name="lead_source"
                  value={readFieldValue(
                    safeLead,
                    "lead_source",
                    readFieldValue(safeLead, "source_host")
                  )}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Industry"
                  name="industry"
                  value={readFieldValue(safeLead, "industry")}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Status"
                  name="status"
                  value={readFieldValue(safeLead, "status")}
                  onValueChange={updateField}
                  options={statusOptions}
                />
              </div>
            </div>

            <div className="info-group info-group--dense">
              <div className="info-section-header">
                <div>
                  <div className="info-section-eyebrow">Lead context</div>
                  <div className="info-section-title">Notes & Summary</div>
                </div>
              </div>

              <div className="info-grid-2 info-grid-tight">
                <InfoField
                  label="Priority Score"
                  name="priority_score"
                  type="number"
                  value={readFieldValue(safeLead, "priority_score")}
                  onValueChange={updateField}
                />
                <div className="info-stat-card">
                  <span className="info-stat-label">Created</span>
                  <strong className="info-stat-value">
                    {safeLead.created_at
                      ? formatShortDate(safeLead.created_at)
                      : "Unknown"}
                  </strong>
                </div>
                <InfoField
                  label="Message"
                  name="notes"
                  textarea
                  className="field-wide"
                  value={leadNotes}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Summary"
                  name="summary"
                  textarea
                  className="field-wide"
                  value={leadSummary}
                  onValueChange={updateField}
                />
              </div>
            </div>

            <div className="info-group info-group--dense">
              <div className="info-section-header">
                <div>
                  <div className="info-section-eyebrow">Contact tracking</div>
                  <div className="info-section-title">Disposition</div>
                </div>
              </div>

              <div className="info-grid-2 info-grid-tight">
                <InfoField
                  label="Last Outcome"
                  name="last_disposition"
                  value={readFieldValue(safeLead, "last_disposition")}
                  onValueChange={updateField}
                  options={[
                    { value: "reached", label: "Reached - Spoke with contact" },
                    { value: "voicemail", label: "Left Voicemail" },
                    { value: "no_answer", label: "No Answer" },
                    { value: "wrong_number", label: "Wrong Number" },
                    { value: "callback", label: "Callback Requested" },
                    { value: "not_interested", label: "Not Interested" },
                    { value: "qualified", label: "Qualified - Moving Forward" },
                  ]}
                />
                <div className="info-stat-card">
                  <span className="info-stat-label">Contact Attempts</span>
                  <strong className="info-stat-value">
                    {readFieldValue(safeLead, "contact_attempts", 0) || 0}
                  </strong>
                </div>
              </div>
            </div>

            <div className="info-group info-group--dense">
              <div className="info-section-header">
                <div>
                  <div className="info-section-eyebrow">Revenue loop</div>
                  <div className="info-section-title">Revenue Attribution</div>
                </div>
              </div>

              <div className="info-grid-2 info-grid-tight">
                <div className="info-stat-card">
                  <span className="info-stat-label">Revenue</span>
                  <strong className="info-stat-value">
                    {formatCurrency(Number(leadAttribution?.revenue_total || 0))}
                  </strong>
                </div>
                <div className="info-stat-card">
                  <span className="info-stat-label">Outstanding</span>
                  <strong className="info-stat-value">
                    {formatCurrency(Number(leadAttribution?.outstanding_balance || 0))}
                  </strong>
                </div>
                <div className="info-stat-card">
                  <span className="info-stat-label">Jobs</span>
                  <strong className="info-stat-value">
                    {Number(leadAttribution?.jobs_count || 0)}
                  </strong>
                </div>
                <div className="info-stat-card">
                  <span className="info-stat-label">Paid Invoices</span>
                  <strong className="info-stat-value">
                    {Number(leadAttribution?.paid_invoices || 0)}
                  </strong>
                </div>
                <div className="info-stat-card">
                  <span className="info-stat-label">Last Service</span>
                  <strong className="info-stat-value">
                    {leadAttribution?.last_service_date
                      ? formatShortDate(leadAttribution.last_service_date)
                      : "—"}
                  </strong>
                </div>
                <div className="info-stat-card">
                  <span className="info-stat-label">Service Frequency</span>
                  <strong className="info-stat-value">
                    {cleanText(leadAttribution?.service_frequency) || "—"}
                  </strong>
                </div>
                <div className="info-stat-card field-wide">
                  <span className="info-stat-label">CLV Estimate</span>
                  <strong className="info-stat-value">
                    {formatCurrency(Number(leadAttribution?.clv_estimate || 0))}
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <div className="info-main-right">
            <div className="info-group info-group--dense info-address-group">
              <div className="info-section-header">
                <div>
                  <div className="info-section-eyebrow">Property</div>
                  <div className="info-section-title">Address & Map</div>
                </div>
                {mapHref ? (
                  <a
                    className="info-map-link"
                    href={mapHref}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Maps
                  </a>
                ) : null}
              </div>

              <div className="info-grid-address-top">
                <InfoField
                  label="Street Address"
                  name="address"
                  className="field-wide"
                  value={readFieldValue(safeLead, "address")}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Zip"
                  name="zip_code"
                  value={readFieldValue(safeLead, "zip_code")}
                  onValueChange={updateField}
                />
              </div>

              <div className="info-grid-4 info-grid-tight">
                <InfoField
                  label="City"
                  name="city"
                  value={readFieldValue(safeLead, "city")}
                  onValueChange={updateField}
                />
                <InfoField
                  label="State"
                  name="state"
                  value={readFieldValue(safeLead, "state")}
                  onValueChange={updateField}
                />
                <InfoField
                  label="County"
                  name="county"
                  value={readFieldValue(safeLead, "county")}
                  onValueChange={updateField}
                />
                <InfoField
                  label="Country"
                  name="country"
                  value={readFieldValue(safeLead, "country", "United States")}
                  onValueChange={updateField}
                />
              </div>

              {hideMap ? null : (
                <div className="info-map">
                  <InfoMapPanel addressQuery={addressQuery} />
                </div>
              )}
            </div>
          </div>
        </div>

        {dynamicFields.length > 0 ? (
          <div className="info-secondary-row">
            <div className="info-group info-group--dense info-secondary-single">
              <div className="info-section-header">
                <div>
                  <div className="info-section-eyebrow">Extended fields</div>
                  <div className="info-section-title">Additional Details</div>
                </div>
              </div>
              <div className="info-grid-3 industry-grid">
                {dynamicFields.map((field) => (
                  <InfoField
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    value={readFieldValue(safeLead, field.name)}
                    onValueChange={updateField}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="info-tab">
      <div className="info-main-row">
        <div className="info-main-left">
          <div className="info-group info-group--dense">
            <div className="info-section-header">
              <div>
                <div className="info-section-eyebrow">Customer profile</div>
                <div className="info-section-title">Contact Information</div>
              </div>
            </div>
            <div className="info-grid-2 info-grid-tight">
              <InfoField
                label="First Name"
                name="first_name"
                value={readFieldValue(safeLead, "first_name")}
                onValueChange={updateField}
              />
              <InfoField
                label="Last Name"
                name="last_name"
                value={readFieldValue(safeLead, "last_name")}
                onValueChange={updateField}
              />
              <InfoField
                label="Company Name"
                name="company_name"
                className="field-wide"
                value={readFieldValue(safeLead, "company_name")}
                onValueChange={updateField}
              />
              <InfoField
                label="Phone"
                name="primary_phone"
                type="tel"
                value={readFieldValue(
                  safeLead,
                  "primary_phone",
                  readFieldValue(safeLead, "phone_number")
                )}
                onValueChange={updateField}
              />
              <InfoField
                label="Email"
                name="primary_email"
                type="email"
                value={readFieldValue(
                  safeLead,
                  "primary_email",
                  readFieldValue(safeLead, "email")
                )}
                onValueChange={updateField}
              />
              <InfoField
                label="Secondary Phone"
                name="secondary_phone"
                type="tel"
                value={readFieldValue(safeLead, "secondary_phone")}
                onValueChange={updateField}
              />
              <InfoField
                label="Secondary Email"
                name="secondary_email"
                type="email"
                value={readFieldValue(safeLead, "secondary_email")}
                onValueChange={updateField}
              />
            </div>
          </div>

          <div className="info-group info-group--dense">
            <div className="info-section-header">
              <div>
                <div className="info-section-eyebrow">Account detail</div>
                <div className="info-section-title">Service & Industry</div>
              </div>
              {isCustomer ? (
                <button
                  className="ai-btn"
                  onClick={runEnrichment}
                  disabled={loadingEnrich}
                >
                  {loadingEnrich ? "Enriching..." : "Enrich"}
                </button>
              ) : null}
            </div>
            <div className="info-grid-2 info-grid-tight">
              <IndustryFields />
              <InfoField
                label="Access Notes"
                name="access_notes"
                textarea
                className="field-wide"
                value={readFieldValue(safeLead, "access_notes")}
                onValueChange={updateField}
              />
              {dynamicFields
                .filter(
                  (f) =>
                    !["first_name", "last_name", "full_name", "name", "address", "city", "state", "zip_code", "county", "country", "primary_phone", "primary_email"].includes(f.name)
                )
                .map((field) => (
                  <InfoField
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    value={readFieldValue(safeLead, field.name)}
                    onValueChange={updateField}
                  />
                ))}
            </div>
            {safeLead.ai?.summary ? (
              <div className="ai-summary" style={{ marginTop: 10 }}>
                {safeLead.ai.summary}
              </div>
            ) : null}
          </div>
        </div>

        <div className="info-main-right">
          <div className="info-group info-group--dense info-address-group">
            <div className="info-section-header">
              <div>
                <div className="info-section-eyebrow">Property</div>
                <div className="info-section-title">Address & Map</div>
              </div>
              {mapHref ? (
                <a
                  className="info-map-link"
                  href={mapHref}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Maps
                </a>
              ) : null}
            </div>

            <div className="info-grid-address-top">
              <InfoField
                label="Street Address"
                name="address"
                className="field-wide"
                value={readFieldValue(safeLead, "address")}
                onValueChange={updateField}
              />
              <InfoField
                label="Zip"
                name="zip_code"
                value={readFieldValue(safeLead, "zip_code")}
                onValueChange={updateField}
              />
            </div>

            <div className="info-grid-4 info-grid-tight">
              <InfoField
                label="City"
                name="city"
                value={readFieldValue(safeLead, "city")}
                onValueChange={updateField}
              />
              <InfoField
                label="State"
                name="state"
                value={readFieldValue(safeLead, "state")}
                onValueChange={updateField}
              />
              <InfoField
                label="County"
                name="county"
                value={readFieldValue(safeLead, "county")}
                onValueChange={updateField}
              />
              <InfoField
                label="Country"
                name="country"
                value={readFieldValue(safeLead, "country")}
                onValueChange={updateField}
              />
            </div>

            {hideMap ? null : (
              <div className="info-map">
                <InfoMapPanel addressQuery={addressQuery} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
